import * as React from 'react';
import { ButtonProps } from './ButtonWidget';
import { ButtonWidget } from './ButtonWidgetShim';
import { TabStyles } from '../../styles/TabStyles';
import { ButtonAttrs } from '../../utils/HtmlAttrs';

export interface AutoResetButtonProps extends ButtonProps {
  /**
   * The amount of time in milliseconds that the `delayLabel` will be displayed after a click occurs.
   * @default 5000
   */
  delay?: number;

  /**
   * The text content, which replaces the `label`, displayed inside the button after a click occurs.
   */
  delayLabel: string;

  /**
   * The function that will be called once the `delay` time has elapsed after a click occurs.
   */
  handleReset: () => void;

  /**
   * The text content displayed inside the button.
   * NOTE: `label` is replaced by `delayLabel` after a click occurs, for a duration of time defined by 'delay'.
   */
  label: string;
}

const DelayStyleOverrides: React.CSSProperties = Object.freeze({
  backgroundColor: TabStyles.Colors.L8,
  borderColor: TabStyles.Colors.L8,
  color: TabStyles.Colors.White
});

export interface AutoResetButtonState {
  label?: string;
}

export class AutoResetButton extends React.PureComponent<AutoResetButtonProps & Omit<ButtonAttrs, 'onClick'>, AutoResetButtonState> {
  public static defaultProps: Pick<AutoResetButtonProps, 'delay'> = { delay: 5000 };
  public state: AutoResetButtonState = { label: undefined };
  private timeoutId: number | undefined = undefined;

  public componentWillUnmount(): void {
    window.clearTimeout(this.timeoutId);
  }

  private handleClick: React.MouseEventHandler<HTMLDivElement & HTMLButtonElement> = e => {
    this.setState({ label: this.props.delayLabel });
    this.timeoutId = window.setTimeout(this.handleReset, this.props.delay);
    // tslint:disable-next-line:deprecation
    this.props.handleClick?.(e);
  }

  private handleReset = (): void => {
    this.timeoutId = undefined;
    this.setState({ label: undefined });
    this.props.handleReset();
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length deprecation
    const { children, delay, delayLabel, handleClick, handleReset, label, onKeyDown, onKeyPress, onKeyUp, onMouseDown, onMouseUp, style, ...rest } = this.props;
    const interactiveEvents = { onClick: this.handleClick, onKeyDown, onKeyPress, onKeyUp, onMouseDown, onMouseUp };
    const isPushed = !!this.state.label;

    return (
      <ButtonWidget
        {...rest}
        {...(!isPushed && interactiveEvents)}
        aria-disabled={rest.disabled || isPushed} // TFS 985328 -- use `aria-disabled` to preserve focus so AT reads the button when pushed
        aria-live='assertive' // aria-live tells AT to re-announce the button when the text changes (i.e. after it gets clicked)
        style={{ ...style, ...(isPushed && DelayStyleOverrides) }}
      >
        {this.state.label || label}
        {children}
      </ButtonWidget>
    );
  }
}
